import { useState, useCallback, useEffect, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { useAuth } from './useAuth';
import { supabase } from '../config/supabase';

export interface UserProfile {
  id: string;
  username: string;
  created_at: string;
  updated_at: string;
  account_status: string;
}

export interface UseAccountManagementReturn {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  refreshUser: () => Promise<void>;
  clearError: () => void;
  isAuthenticated: boolean;
}

/**
 * Custom hook for managing user account data and operations
 * Provides centralized user data management with error handling and loading states
 * Requirements: 5.2, 5.4
 */
export const useAccountManagement = (): UseAccountManagementReturn => {
  const { user, loading: authLoading } = useAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastFetchedUserIdRef = useRef<string | null>(null);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch user profile data from Supabase
  const fetchUserProfile = useCallback(async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, username, created_at, updated_at, account_status')
        .eq('id', userId)
        .single();

      if (error) {
        throw new Error(`Failed to fetch user profile: ${error.message}`);
      }

      return data as UserProfile;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch user profile';
      throw new Error(errorMessage);
    }
  }, []); // Empty dependency array since this function doesn't depend on any external values

  // Refresh user data
  const refreshUser = useCallback(async () => {
    if (!user) {
      setError('No user logged in');
      setUserProfile(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch fresh user session data
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        throw new Error(`Failed to refresh session: ${sessionError.message}`);
      }

      // Fetch user profile data
      const profile = await fetchUserProfile(user.id);
      setUserProfile(profile);
      lastFetchedUserIdRef.current = user.id;

      // Check if user data has changed and update accordingly
      if (session?.user && session.user.id === user.id) {
        // User data is managed by AuthContext, so we don't need to update it here
        // The AuthContext will handle session updates automatically
      }
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh user data';
      setError(errorMessage);
      setUserProfile(null);
    } finally {
      setLoading(false);
    }
  }, [user, fetchUserProfile]);

  // Fetch user profile when user changes
  useEffect(() => {
    setError(null);
    
    if (user) {
      // Only fetch if we haven't already fetched for this user
      if (lastFetchedUserIdRef.current !== user.id) {
        lastFetchedUserIdRef.current = user.id;
        
        fetchUserProfile(user.id)
          .then(profile => {
            setUserProfile(profile);
          })
          .catch(err => {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load user profile';
            setError(errorMessage);
            setUserProfile(null);
          });
      }
    } else {
      setUserProfile(null);
      lastFetchedUserIdRef.current = null;
    }
  }, [user, fetchUserProfile]);

  // Listen for auth state changes to clear profile data
  useEffect(() => {
    const handleAuthChange = () => {
      if (!user) {
        setUserProfile(null);
        setError(null);
        lastFetchedUserIdRef.current = null;
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('wallstreetle-auth-changed', handleAuthChange);
      return () => {
        window.removeEventListener('wallstreetle-auth-changed', handleAuthChange);
      };
    }
  }, [user]);

  const isAuthenticated = !!user && !!userProfile;

  return {
    user,
    userProfile,
    loading: authLoading || loading,
    error,
    refreshUser,
    clearError,
    isAuthenticated
  };
};